import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {YesNoModal} from '../../../elements/YesNoModal';
import {MemoryAutonomyControl} from '../../memory/MemoryAutonomyControl';

/**
 * El rail del mazo: cuántos esperan, cuánto llevás hecho en esta sesión y el atajo para barrer todo.
 *
 * Dos correcciones deliberadas al mockup:
 *
 * 1. **La barra de progreso es NEUTRA.** En el mockup es verde y se llena igual si aprobás que si
 *    rechazás, o sea que la pantalla te felicita por descartar. El avance de una revisión es
 *    "decidido / sin decidir" —eso es lo que mide la barra—, y el desglose de abajo dice cuántos
 *    fueron para cada lado. Un color de éxito sobre las dos decisiones empuja a aprobar para que la
 *    barra suba, que es exactamente lo que un gate humano no debe premiar.
 *
 * 2. **"Aprobar todo" pregunta antes.** El mockup dispara 7 aprobaciones encadenadas con un click:
 *    es la única acción de la pantalla que compromete N hechos de una y la más fácil de apretar sin
 *    querer. La confirmación dice CUÁNTOS son, que es el dato que falta para decidir.
 *
 * ⚠️ **Contrato de los números**: `pending` y `approved`/`rejected` tienen que hablar del MISMO
 * universo, porque la barra es `decididos / (decididos + pendientes)`. Ese universo es la cola
 * PRINCIPAL del mazo: las cartas que esperan una decisión de a una. NO entran el ruido probable (se
 * descarta por grupo y se cuenta aparte en `session.noise`), ni los «Quitar» de «Entró solo»
 * (`session.removed`: venían de la biblioteca, nunca estuvieron en la cola), ni «Revisar» y
 * «Restaurar» (no deciden nada: devuelven a la cola). Sumar cualquiera de esos a `rejected` infla el
 * avance sin haber decidido una carta. `pending` es el mismo número que el umbral "N esperan tu ok"
 * de arriba (`pendingCount` del store, sin ruido). `approveAllCount` es otra cosa y por eso viaja
 * aparte: es cuántos resuelve ESE botón (sólo el mazo, y acotado al tope del bulk del backend), no
 * cuántos faltan.
 */
export function ReviewRail({
    pending, approved, rejected, approveAllCount, busy, onApproveAll,
    autonomy, onAutonomyChange, autonomySaving = false, autonomyKnobRef,
    // El atajo se OCULTA cuando el panel de al lado no es la cola principal (hoy: el modo «Ruido
    // probable»). Con el ruido como modo, el rail quedo a la altura de un panel que muestra otra
    // cosa, y «Aprobar todo» ahi aprueba los N de la cola principal —que no estan en pantalla—
    // leyendose como «aprobar todo esto». El numero y la barra SI se quedan: son el estado del
    // acto, no una accion sobre lo que se esta mirando. Review 10/09 (C-revisa-02) + QA-VIS-10.
    showShortcut = true,
}) {
    const {t} = useTranslation();
    const [confirming, setConfirming] = useState(false);

    const done = approved + rejected;
    const total = done + pending;
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    const canApproveAll = approveAllCount > 0 && !busy;

    return (
        <aside className="iabr-rail">
            <div className="iabr-railcard">
                <div className="iabr-railnum">{pending}</div>
                <h4>{t('ia_booster.rail.pending_label')}</h4>
                <p>{t('ia_booster.rail.pending_text')}</p>
                <div className="iabr-prog" role="progressbar"
                     aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}
                     aria-label={t('ia_booster.rail.progress_label')}>
                    <div className="iabr-prog-fill" style={{width: `${pct}%`}}/>
                </div>
                {done > 0 && (
                    <p className="iabr-breakdown">
                        {/* Dos números independientes: el plural de i18next se resuelve por UNA
                            variable, así que cada mitad se traduce por su cuenta y se componen. */}
                        {`${t('ia_booster.rail.breakdown_approved', {count: approved})} · `
                            + t('ia_booster.rail.breakdown_rejected', {count: rejected})}
                    </p>
                )}
            </div>

            {showShortcut && (
                <div className="iabr-railcard">
                    <h4>{t('ia_booster.rail.shortcut_title')}</h4>
                    <p>{t('ia_booster.rail.approve_all_hint')}</p>
                    <button type="button" className="iabr-btn -primary iabr-rail-cta"
                            disabled={!canApproveAll} onClick={() => setConfirming(true)}>
                        {t('ia_booster.rail.approve_all')}
                    </button>
                </div>
            )}

            {/* El knob «¿Cuánto aprueba Dashbo solo?» (cards 1EW5v3Ba + 8uu3IdNd). `autonomy`
                ausente = el backend todavía no lo sirve (deploy escalonado) o la agencia no tiene
                el nivel TOTAL: la tarjeta no se dibuja y el rail queda exactamente como estaba.

                🔴 Se muestra el nivel EFECTIVO (`autonomy.effective`), no el override crudo, y no
                hay ni interruptor «Como la agencia» ni link al ajuste global: desde el 08/09 el
                knob es POR CLIENTE y punto (card 8uu3IdNd, que cambia la decisión del 07/09). El
                default de la agencia sigue existiendo en el backend como valor oculto — lo que
                cambió es que ya no se configura desde acá. */}
            {autonomy && (
                <div className="iabr-railcard">
                    <MemoryAutonomyControl
                        compact
                        value={autonomy.effective}
                        canSetFull={autonomy.canSetFull}
                        saving={autonomySaving}
                        onChange={onAutonomyChange}
                        knobRef={autonomyKnobRef}
                    />
                </div>
            )}

            <YesNoModal
                skipRenderButton
                isOpen={confirming}
                title={t('ia_booster.rail.approve_all_confirm_title')}
                question={t('ia_booster.rail.approve_all_confirm_question', {count: approveAllCount})}
                isSaving={busy}
                onYesAnswer={() => {
                    setConfirming(false);
                    onApproveAll();
                }}
                onNoAnswer={() => setConfirming(false)}
            />
        </aside>
    );
}

export default ReviewRail;
